import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Layers, Loader2, Search } from 'lucide-react';
import { productionApi } from '../api';
import SkeletonLoader from './components/SkeletonLoader';
import ApiButton from './components/ApiButton';
import { BREAKPOINTS } from './productionTheme';

const LABEL_WIDTH = 240;
const CHART_WIDTH = 980;

const STATUS_STYLE = {
  LOCKED: { bar: 'bg-slate-400', text: 'text-slate-700', label: 'LOCKED' },
  PENDING: { bar: 'bg-sky-300', text: 'text-sky-700', label: 'PENDING' },
  IN_PROGRESS: { bar: 'bg-blue-600 animate-pulse', text: 'text-blue-700', label: 'IN_PROGRESS' },
  QC_PENDING: { bar: 'bg-orange-500', text: 'text-orange-700', label: 'QC_PENDING' },
  QC_APPROVED: { bar: 'bg-emerald-600', text: 'text-emerald-700', label: 'QC_APPROVED' },
  QC_REJECTED: { bar: 'bg-red-600', text: 'text-red-700', label: 'QC_REJECTED' },
};

const getStatusStyle = (status) => STATUS_STYLE[status] || STATUS_STYLE.LOCKED;

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const deriveDurationMinutes = (stage, start, end) => {
  if (start && end) {
    return Math.max(Math.round((end.getTime() - start.getTime()) / 60000), 1);
  }
  const actual = Number(stage.actual_duration_minutes);
  if (Number.isFinite(actual) && actual > 0) return actual;
  const expected = Number(stage.expected_duration_minutes);
  if (Number.isFinite(expected) && expected > 0) return expected;
  return 30;
};

const fmtDateTime = (value) => {
  if (!value) return 'N/A';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtMinutes = (minutes) => {
  if (!minutes && minutes !== 0) return 'N/A';
  if (minutes < 60) return `${minutes}m`;
  const hr = Math.floor(minutes / 60);
  const min = minutes % 60;
  return min ? `${hr}h ${min}m` : `${hr}h`;
};

const normalizeTimelineResponse = (payload) => {
  const source = payload?.data && !payload?.job_card ? payload.data : payload;
  const job = source?.job_card || source?.jobCard || {};
  const stages = Array.isArray(source?.stages) ? source.stages : [];
  return {
    success: true,
    job_card: {
      id: job.id || job.job_card_id || null,
      job_card_no: job.job_card_no || null,
      design_name: job.design_name || null,
      status: job.status || null,
      quantity: job.quantity ?? null,
      created_at: job.created_at || null,
    },
    stages: stages.map((s) => ({
      ...s,
      sequence_number: s.sequence_number ?? s.sequence_no ?? 0,
      expected_duration_minutes: s.expected_duration_minutes ?? null,
      actual_duration_minutes: s.actual_duration_minutes ?? null,
      delay_minutes: s.delay_minutes ?? 0,
      worker_name: s.worker_name || null,
    })),
  };
};

function TimelineSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <SkeletonLoader count={6} height={56} />
    </div>
  );
}

export default function JobCardGantt({ jobCardId: propJobCardId = null }) {
  const navigate = useNavigate();
  const { jobCardId: routeJobCardId } = useParams();
  const [searchParams] = useSearchParams();

  const fixedJobCardId = propJobCardId || routeJobCardId || searchParams.get('jobCardId') || null;

  const [allJobCards, setAllJobCards] = useState([]);
  const [search, setSearch] = useState('');
  const [timelineData, setTimelineData] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [error, setError] = useState('');
  const [hover, setHover] = useState(null);

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await productionApi.get('/job-cards');
      setAllJobCards(res.data?.data || []);
    } catch (err) {
      console.error(err);
      setAllJobCards([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadTimeline = async (jobId) => {
    if (!jobId) return;
    setLoadingTimeline(true);
    setError('');
    try {
      const endpointCandidates = [
        `/production/stage-timeline/${jobId}`,
        `/job-cards/${jobId}/timeline`,
        `/dashboard/job-card-timeline/${jobId}`,
        `/production/dashboard/job-card-timeline/${jobId}`,
      ];
      let data = null;
      let lastError = null;

      for (const endpoint of endpointCandidates) {
        try {
          const res = await productionApi.get(endpoint);
          const payload = res?.data;
          const normalized = normalizeTimelineResponse(payload);
          if (normalized?.stages?.length || normalized?.job_card?.id) {
            data = normalized;
            break;
          }
        } catch (endpointError) {
          lastError = endpointError;
        }
      }

      if (data) {
        setTimelineData(data);
      } else {
        setTimelineData(null);
        const fallbackMessage = lastError?.response?.data?.message || lastError?.message || 'Unable to load timeline.';
        setError(fallbackMessage);
      }
    } catch (err) {
      console.error(err);
      setTimelineData(null);
      setError(err?.response?.data?.message || 'Failed to fetch timeline data.');
    } finally {
      setLoadingTimeline(false);
    }
  };

  useEffect(() => {
    if (!fixedJobCardId) {
      loadJobs();
      return;
    }
    loadTimeline(Number(fixedJobCardId));
  }, [fixedJobCardId]);

  const filteredJobCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allJobCards.slice(0, 40);
    return allJobCards
      .filter((jc) => {
        const jcNo = (jc.job_card_no || '').toLowerCase();
        const design = (jc.design_code || jc.design_name || '').toLowerCase();
        const order = (jc.order_id || '').toLowerCase();
        return jcNo.includes(q) || design.includes(q) || order.includes(q) || String(jc.id || jc.job_card_id || '').includes(q);
      })
      .slice(0, 40);
  }, [allJobCards, search]);

  const normalizedStages = useMemo(() => {
    const stages = timelineData?.stages || [];
    if (!stages.length) return [];

    const sorted = [...stages].sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));

    let cursor = null;
    return sorted.map((stage, idx) => {
      const expected = Number(stage.expected_duration_minutes || 0);
      let start = parseDate(stage.started_at);
      const endFromApi = parseDate(stage.completed_at);
      let duration = deriveDurationMinutes(stage, start, endFromApi);

      if (!start && cursor) start = new Date(cursor);
      if (!start && endFromApi) start = new Date(endFromApi.getTime() - duration * 60 * 1000);
      if (!start) {
        const fallbackBase = parseDate(timelineData?.job_card?.created_at) || new Date();
        start = new Date(fallbackBase.getTime() + idx * 30 * 60 * 1000);
      }

      let end = endFromApi;
      if (!end) end = new Date(start.getTime() + duration * 60 * 1000);
      duration = deriveDurationMinutes(stage, start, end);

      cursor = end;

      const delay = Number(stage.delay_minutes || 0);

      return {
        ...stage,
        expected_duration_minutes: expected,
        actual_duration_minutes: duration || null,
        effective_duration_minutes: duration,
        delay_minutes: delay,
        display_start: start,
        display_end: end,
      };
    });
  }, [timelineData]);

  const bounds = useMemo(() => {
    if (!normalizedStages.length) return null;
    const starts = normalizedStages.map((s) => s.display_start.getTime());
    const ends = normalizedStages.map((s) => s.display_end.getTime());
    let min = Math.min(...starts);
    let max = Math.max(...ends);
    if (max <= min) max = min + 60 * 60 * 1000;
    return { min, max, span: max - min };
  }, [normalizedStages]);

  const ticks = useMemo(() => {
    if (!bounds) return [];
    const count = 6;
    return Array.from({ length: count + 1 }, (_, idx) => {
      const ratio = idx / count;
      const time = new Date(bounds.min + ratio * bounds.span);
      return { ratio, label: time.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit' }) };
    });
  }, [bounds]);

  const todayRatio = useMemo(() => {
    if (!bounds) return null;
    const now = Date.now();
    if (now < bounds.min || now > bounds.max) return null;
    return (now - bounds.min) / bounds.span;
  }, [bounds]);

  const resetSelection = () => {
    setTimelineData(null);
    setError('');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @media (max-width: ${BREAKPOINTS.mobile}) {
          .gantt-header-wrap { padding-left: 12px !important; padding-right: 12px !important; }
        }
      `}</style>
      <div className="h-16 bg-white border-b border-slate-200 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-3 text-sm">
          <button onClick={() => navigate('/production')} className="text-slate-500 hover:text-indigo-600 flex items-center gap-1">
            <ArrowLeft size={16} /> Back
          </button>
          <span className="text-slate-300">/</span>
          <span className="font-semibold text-slate-800">Job Card Gantt</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Layers className="text-indigo-600" size={24} /> Job Card Timeline
          </h1>
          <p className="text-sm text-slate-600 mt-1">Visual stage-wise production timeline with planned vs actual execution.</p>
        </div>

        {!fixedJobCardId && !timelineData && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 border border-slate-300 rounded-xl px-3 py-2 max-w-lg">
              <Search size={16} className="text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by job card no, order id, design, id"
                className="w-full outline-none text-sm"
              />
            </div>
            {loadingJobs ? (
              <div className="mt-3 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading job cards...</div>
            ) : (
              <div className="mt-3 max-h-80 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                {filteredJobCards.length ? filteredJobCards.map((jc) => {
                  const id = jc.id || jc.job_card_id;
                  return (
                    <button
                      key={id}
                      onClick={() => loadTimeline(id)}
                      className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors"
                    >
                      <div className="font-semibold text-slate-800 text-sm">{jc.job_card_no || `JC-${id}`}</div>
                      <div className="text-xs text-slate-500">{jc.design_name || jc.design_code || '-'} • {jc.order_id || 'No order id'}</div>
                    </button>
                  );
                }) : <div className="px-4 py-5 text-sm text-slate-500">No job cards found.</div>}
              </div>
            )}
          </div>
        )}

        {loadingTimeline && <TimelineSkeleton />}

        {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

        {timelineData && !loadingTimeline && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="text-lg font-bold text-slate-900">{timelineData.job_card?.job_card_no} - {timelineData.job_card?.design_name}</div>
                <div className="text-sm text-slate-600">Status: <span className="font-semibold">{timelineData.job_card?.status}</span> • Qty: {timelineData.job_card?.quantity ?? '-'}</div>
              </div>
              {!fixedJobCardId && (
                <ApiButton onClick={resetSelection} variant="primary" className="px-3 py-2 text-sm w-fit">
                  Choose Another Job Card
                </ApiButton>
              )}
            </div>

            {!normalizedStages.length ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">No stages found for this job card.</div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
                <div className="mb-3 flex flex-wrap gap-3 text-[11px] font-semibold">
                  {Object.entries(STATUS_STYLE).map(([status, cfg]) => (
                    <span key={status} className="inline-flex items-center gap-1.5 text-slate-600">
                      <span className={`w-2.5 h-2.5 rounded-full ${cfg.bar.replace(' animate-pulse', '')}`} />
                      {status}
                    </span>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  <div style={{ minWidth: LABEL_WIDTH + CHART_WIDTH + 36 }}>
                    <div className="flex items-end mb-2">
                      <div style={{ width: LABEL_WIDTH }} className="text-[11px] font-bold uppercase text-slate-500 px-2">Stage / Worker</div>
                      <div style={{ width: CHART_WIDTH }} className="relative h-10 border-l border-r border-slate-200">
                        {ticks.map((tick) => (
                          <div key={tick.ratio} className="absolute top-0 bottom-0" style={{ left: `${tick.ratio * 100}%` }}>
                            <div className="w-px h-full bg-slate-200" />
                            <div className="absolute -bottom-1 -translate-x-1/2 text-[10px] text-slate-500 whitespace-nowrap">{tick.label}</div>
                          </div>
                        ))}
                        {todayRatio !== null && (
                          <div className="absolute top-0 bottom-0" style={{ left: `${todayRatio * 100}%` }}>
                            <div className="w-px h-full bg-fuchsia-500" />
                            <div className="absolute -top-4 -translate-x-1/2 text-[10px] text-fuchsia-600 font-bold">Today</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 mt-6">
                      {normalizedStages.map((stage) => {
                        const startRatio = bounds ? (stage.display_start.getTime() - bounds.min) / bounds.span : 0;
                        const widthRatio = bounds ? (stage.display_end.getTime() - stage.display_start.getTime()) / bounds.span : 0.1;
                        const delayRatio = bounds && stage.delay_minutes > 0 ? (stage.delay_minutes * 60 * 1000) / bounds.span : 0;
                        const style = getStatusStyle(stage.status);

                        return (
                          <div key={stage.id} className="flex items-center">
                            <div style={{ width: LABEL_WIDTH }} className="px-2 pr-4">
                              <div className="text-sm font-semibold text-slate-800 truncate">{stage.stage_name}</div>
                              <div className="text-[11px] text-slate-500 truncate">{stage.worker_name || 'Unassigned'}</div>
                            </div>

                            <div style={{ width: CHART_WIDTH }} className="relative h-10 border border-slate-100 bg-slate-50/60 rounded-md">
                              <div
                                className={`absolute top-1.5 h-7 rounded-md ${style.bar} shadow-sm cursor-pointer`}
                                style={{ left: `${Math.max(startRatio * 100, 0)}%`, width: `${Math.max(widthRatio * 100, 2)}%` }}
                                onMouseEnter={(e) => setHover({ stage, x: e.clientX, y: e.clientY })}
                                onMouseMove={(e) => setHover({ stage, x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => setHover(null)}
                              />

                              {stage.delay_minutes > 0 && (
                                <>
                                  <div
                                    className="absolute top-2 h-6 bg-red-500/85 rounded-r-md"
                                    style={{
                                      left: `${Math.max((startRatio + widthRatio) * 100, 0)}%`,
                                      width: `${Math.max(delayRatio * 100, 0.8)}%`,
                                    }}
                                    onMouseEnter={(e) => setHover({ stage, x: e.clientX, y: e.clientY })}
                                    onMouseMove={(e) => setHover({ stage, x: e.clientX, y: e.clientY })}
                                    onMouseLeave={() => setHover(null)}
                                  />
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-red-700 bg-white/80 px-1 rounded">
                                    Delayed {stage.delay_minutes} min
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {hover && (
        <div
          className="fixed z-50 pointer-events-none rounded-lg border border-slate-200 bg-white shadow-xl px-3 py-2 text-xs"
          style={{ left: hover.x + 12, top: hover.y + 12 }}
        >
          <div className="font-bold text-slate-800 mb-1">{hover.stage.stage_name}</div>
          <div className="text-slate-600">Worker: {hover.stage.worker_name || 'Unassigned'}</div>
          <div className="text-slate-600">Status: {hover.stage.status || 'LOCKED'}</div>
          <div className="text-slate-600">Start: {fmtDateTime(hover.stage.display_start)}</div>
          <div className="text-slate-600">End: {fmtDateTime(hover.stage.display_end)}</div>
          <div className="text-slate-600">Duration: {fmtMinutes(hover.stage.effective_duration_minutes)}</div>
          <div className="text-slate-600">Planned: {fmtMinutes(hover.stage.expected_duration_minutes || 0)}</div>
        </div>
      )}
    </div>
  );
}
